import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { MotorDataDataService } from "src/app/share/data-service/motor.data.service";
import { ProductDocumentInterface } from "src/app/share/interface/motor.interface";
import { PartnerService } from "src/app/share/service/partner.service";

@Component({
  selector: "app-insurer-card-v2",
  templateUrl: "./insurer-card-v2.component.html",
  styleUrls: ["./insurer-card-v2.component.scss"]
})
export class InsurerCardV2Component implements OnInit {
  @Input() first: boolean = false;
  @Input() item: IInsurerData;
  @Input() showTag: boolean = false;
  @Input() insuranceType: string;
  @Input() ehailingUsed?: boolean = false;
  @Input() vehicleType: string;
  @Output() onSubmit = new EventEmitter();

  benefits: IBenefit[] = [];
  showMoreBenefits: boolean = true;
  documents: ProductDocumentInterface[] = [];
  totalBenefits: number;

  partnerCode: string;

  constructor(
    private motorDataDataService: MotorDataDataService,
    public translate: TranslateService,
    private partnerService: PartnerService
  ) {
    this.translate.onLangChange.subscribe((_event: LangChangeEvent) =>
      this.getProductDocument()
    );
  }

  ngOnInit(): void {
    this.getProductDocument();
    this.displayBenefits();
    this.getPartnerCode();
  }

  private getPartnerCode() {
    this.partnerCode = this.partnerService.getPartnerCode();
    !this.partnerCode ? (this.partnerCode = "ps") : null;
  }

  hasTranslation(key: string): boolean {
    const translation = this.translate.instant(key);
    if (translation.length) return true;
    return false;
  }

  toggleMoreBenefits() {
    if (this.showMoreBenefits) {
      this.showMoreBenefits = false;
    } else {
      this.showMoreBenefits = true;
    }

    this.displayBenefits();
  }

  getRibbonType(partnerName: string): string {
    let ribbonType: string;
    const storageUrl: string =
      "https://storage.googleapis.com/ps-asset/affiliate/logo/";

    const dailyAndAnnual = [
      "Allianz",
      "AXA",
      "Zurich",
      "Zurich_Takaful",
      "GrabAXA"
    ];
    const annualOnly = ["Sompo"];
    const dailyOnly = ["Etiqa", "Etiqa_Takaful", "MSIG", "Chubb", "TMIM"];

    if (dailyAndAnnual.includes(partnerName)) ribbonType = "daily_annual";
    if (annualOnly.includes(partnerName)) ribbonType = "annual";
    if (dailyOnly.includes(partnerName)) ribbonType = "daily";

    if (
      [...dailyAndAnnual, ...annualOnly, ...dailyOnly].includes(partnerName)
    ) {
      return `${storageUrl}${this.partnerCode}_${ribbonType}_${this.translate.currentLang}.png`;
    } else {
      return "assets/image/transparent.png";
    }
  }

  private displayBenefits() {
    this.getPartnerCode();
    let benefitList;

    if (this.vehicleType === "Motorcycle") {
      benefitList = INSURER_BENEFITS_LIST_MOTORCYCLE;
    } else if (this.vehicleType === "Lorry") {
      benefitList = INSURER_BENEFITS_LIST_LORRY;
    } else {
      benefitList =
        this.insuranceType === "TPFT"
          ? INSURER_BENEFITS_LIST_TPFT
          : INSURER_BENEFITS_LIST;
    }

    let tempBenefits: { type: string; description: string }[] = benefitList[
      this.item.code
    ].filter((x) => this.hasTranslation(x.description));

    //totalBenefits is used to show user the total number of benefits there are
    // before user clicks on "See all benefits"
    this.totalBenefits = tempBenefits.length;

    if (this.showMoreBenefits) {
      this.benefits = tempBenefits;
    } else {
      this.benefits = tempBenefits.slice(0, 4);
    }
  }

  //Note: Ideally this API should not be called in a presentational component.
  //To be refactored in the parent component and passed in under a modified version of "item"
  private getProductDocument() {
    if (!this.item) return;
    this.getPartnerCode();

    if (this.partnerCode === "foodpanda") {
      this.motorDataDataService
        .getMotorcycleProductDocumentById(
          this.item.productId,
          this.translate.currentLang,
          this.item.typeOfSum
        )
        .subscribe({
          next: (x) => {
            this.documents = x;
          }
        });
    } else {
      this.motorDataDataService
        .getProductDocumentById(
          this.item.productId,
          this.translate.currentLang,
          this.item.typeOfSum
        )
        .subscribe({
          next: (x) => {
            this.documents = x;
          }
        });
    }
  }

  //NOTE: To update accordingly. What's the number that we should compare with?
  get eppEligibility() {
    let eppEligibilityObject: { isEligible: boolean; months: number } = null;
    const eppAmount = this.totalBasePremiumAfterRebate;

    if (eppAmount >= 500 && eppAmount < 999) {
      eppEligibilityObject = { isEligible: true, months: 6 };
    }

    if (eppAmount > 999) {
      eppEligibilityObject = { isEligible: true, months: 12 };
    }

    return eppEligibilityObject;
  }

  get totalBasePremiumAfterRebate() {
    return this.item?.totalBasePremium - this.item?.rebate || 0;
  }

  get rebateAmount() {
    return this.item?.rebate;
  }

  get totalPrice() {
    return this.totalCharges + this.item?.serviceTaxRate * this.totalCharges;
  }

  get totalBasePremium() {
    return this.item?.totalBasePremium;
  }

  get basePremium() {
    return this.item?.basePremium;
  }

  get totalCharges() {
    return (
      this.totalBasePremium +
      (this.item?.taxRate * this.totalBasePremium + this.item?.stampDutyPrice)
    );
  }

  handleBuyNow() {
    this.onSubmit.emit(this.item.id);
  }
}

export interface IInsurerData {
  image: string;
  typeOfSum: string;
  sumInsured: number;
  sumInsuredRange?: string;
  code: string;
  total: number;
  id: string;
  basePremium: number;
  totalBasePremium: number;
  serviceTaxRate: number;
  taxRate: number;
  stampDutyPrice: number;
  defaultExcessDiscount?: number;
  rebate: number;
  productId: string;
  isTakaful: boolean;
}

export interface IBenefit {
  type: string;
  description: string;
}

//NOTE: This list does not need to be updated if the copywriting of the benefit changes
//UNLESS new type of benefit is included or removed completely.
//Any changes in copywriting should only be updated at the i18n file.
export const INSURER_BENEFITS_LIST = {
  PolicyStreet: [
    { type: "driver", description: "benefits.PolicyStreet.additionalDriver" },
    {
      type: "24hrTowing",
      description: "benefits.PolicyStreet.emergencyTowing"
    },
    { type: "free", description: "benefits.PolicyStreet.batteryService" },
    { type: "free", description: "benefits.PolicyStreet.tankService" },
    { type: "free", description: "benefits.PolicyStreet.tyreService" },
    { type: "free", description: "benefits.PolicyStreet.roadsideAssistance" },
    { type: "free", description: "benefits.PolicyStreet.claimAssistance" },
    { type: "free", description: "benefits.PolicyStreet.lockSmith" },
    { type: "", description: "benefits.PolicyStreet.eHailing" },
    { type: "", description: "benefits.PolicyStreet.instantApproval" },
    { type: "", description: "benefits.PolicyStreet.easyAndFastClaims" },
    { type: "", description: "benefits.PolicyStreet.coverForFEL" },
    { type: "", description: "benefits.PolicyStreet.coverForTheft" },
    { type: "", description: "benefits.PolicyStreet.oldVehicles" },
    { type: "", description: "benefits.PolicyStreet.cashback" },
    { type: "", description: "benefits.PolicyStreet.others" }
  ],
  AXA: [
    { type: "driver", description: "benefits.AXA.additionalDriver" },
    { type: "24hrTowing", description: "benefits.AXA.emergencyTowing" },
    { type: "free", description: "benefits.AXA.batteryService" },
    { type: "free", description: "benefits.AXA.tankService" },
    { type: "free", description: "benefits.AXA.tyreService" },
    { type: "free", description: "benefits.AXA.roadsideAssistance" },
    { type: "free", description: "benefits.AXA.claimAssistance" },
    { type: "free", description: "benefits.AXA.lockSmith" },
    { type: "", description: "benefits.AXA.eHailing" },
    { type: "", description: "benefits.AXA.instantApproval" },
    { type: "", description: "benefits.AXA.easyAndFastClaims" },
    { type: "", description: "benefits.AXA.coverForFEL" },
    { type: "", description: "benefits.AXA.coverForTheft" },
    { type: "", description: "benefits.AXA.oldVehicles" },
    { type: "", description: "benefits.AXA.cashback" },
    { type: "", description: "benefits.AXA.others" }
  ],
  Generali: [
    { type: "driver", description: "benefits.Generali.additionalDriver" },
    { type: "24hrTowing", description: "benefits.Generali.emergencyTowingg" },
    { type: "free", description: "benefits.Generali.batteryService" },
    { type: "free", description: "benefits.Generali.tankService" },
    { type: "free", description: "benefits.Generali.tyreService" },
    { type: "free", description: "benefits.Generali.roadsideAssistance" },
    { type: "free", description: "benefits.Generali.claimAssistance" },
    { type: "free", description: "benefits.Generali.lockSmith" },
    { type: "", description: "benefits.Generali.eHailing" },
    { type: "", description: "benefits.Generali.instantApproval" },
    { type: "", description: "benefits.Generali.easyAndFastClaims" },
    { type: "", description: "benefits.Generali.coverForFEL" },
    { type: "", description: "benefits.Generali.coverForTheft" },
    { type: "", description: "benefits.Generali.oldVehicles" },
    { type: "", description: "benefits.Generali.cashback" },
    { type: "", description: "benefits.Generali.others" }
  ],
  GrabAXA: [
    { type: "free", description: "benefits.AXAGrab.warranty" },
    { type: "free", description: "benefits.AXAGrab.roadsideAssistance" },
    { type: "free", description: "benefits.AXAGrab.rideupTo30KM" },
    { type: "free", description: "benefits.AXAGrab.axaPreferredWorkshop" },
    { type: "", description: "benefits.AXAGrab.instantApproval" },
    { type: "", description: "benefits.AXAGrab.fastClaimApproval" },
    { type: "", description: "benefits.AXAGrab.personalAccidentCarOwner" },
    { type: "", description: "benefits.AXAGrab.medicalClaims" },
    { type: "", description: "benefits.AXAGrab.lossOfIncome" },
    { type: "", description: "benefits.AXAGrab.loanProtection" },
    { type: "", description: "benefits.AXAGrab.wheelAlignment" }
  ],
  Allianz: [
    { type: "driver", description: "benefits.Allianz.additionalDriver" },
    { type: "24hrTowing", description: "benefits.Allianz.emergencyTowing" },
    { type: "free", description: "benefits.Allianz.batteryService" },
    { type: "free", description: "benefits.Allianz.tankService" },
    { type: "free", description: "benefits.Allianz.tyreService" },
    { type: "free", description: "benefits.Allianz.roadsideAssistance" },
    { type: "free", description: "benefits.Allianz.roadsideRepair" },
    { type: "free", description: "benefits.Allianz.claimAssistance" },
    { type: "free", description: "benefits.Allianz.lockSmith" },
    { type: "", description: "benefits.Allianz.eHailing" },
    { type: "", description: "benefits.Allianz.instantApproval" },
    { type: "", description: "benefits.Allianz.easyAndFastClaims" },
    { type: "", description: "benefits.Allianz.coverForFEL" },
    { type: "", description: "benefits.Allianz.coverForTheft" },
    { type: "", description: "benefits.Allianz.oldVehicles" },
    { type: "", description: "benefits.Allianz.cashback" },
    { type: "", description: "benefits.Allianz.others" }
  ],
  Zurich: [
    { type: "driver", description: "benefits.Zurich.additionalDriver" },
    { type: "24hrTowing", description: "benefits.Zurich.emergencyTowing" },
    { type: "free", description: "benefits.Zurich.batteryService" },
    { type: "free", description: "benefits.Zurich.tankService" },
    { type: "free", description: "benefits.Zurich.tyreService" },
    { type: "free", description: "benefits.Zurich.roadsideAssistance" },
    { type: "free", description: "benefits.Zurich.roadsideRepair" },
    { type: "free", description: "benefits.Zurich.claimAssistance" },
    { type: "free", description: "benefits.Zurich.lockSmith" },
    { type: "free", description: "benefits.Zurich.windscreenCoverage" },
    { type: "", description: "benefits.Zurich.eHailing" },
    { type: "", description: "benefits.Zurich.instantApproval" },
    { type: "", description: "benefits.Zurich.easyAndFastClaims" },
    { type: "", description: "benefits.Zurich.coverForFEL" },
    { type: "", description: "benefits.Zurich.coverForTheft" },
    { type: "", description: "benefits.Zurich.oldVehicles" },
    { type: "", description: "benefits.Zurich.cashback" },
    { type: "", description: "benefits.Zurich.others" }
  ],
  Zurich_Takaful: [
    {
      type: "24hrTowing",
      description: "benefits.Zurich_Takaful.emergencyTowing"
    },
    { type: "free", description: "benefits.Zurich_Takaful.batteryService" },
    { type: "free", description: "benefits.Zurich_Takaful.tankService" },
    { type: "free", description: "benefits.Zurich_Takaful.tyreService" },
    { type: "free", description: "benefits.Zurich_Takaful.roadsideAssistance" },
    { type: "free", description: "benefits.Zurich_Takaful.roadsideRepair" },
    { type: "free", description: "benefits.Zurich_Takaful.claimAssistance" },
    { type: "free", description: "benefits.Zurich_Takaful.lockSmith" },
    { type: "free", description: "benefits.Zurich_Takaful.windscreenCoverage" },
    { type: "", description: "benefits.Zurich_Takaful.eHailing" },
    { type: "", description: "benefits.Zurich_Takaful.instantApproval" },
    { type: "", description: "benefits.Zurich_Takaful.easyAndFastClaims" },
    { type: "", description: "benefits.Zurich_Takaful.coverForFEL" },
    { type: "", description: "benefits.Zurich_Takaful.coverForTheft" },
    { type: "", description: "benefits.Zurich_Takaful.oldVehicles" },
    { type: "", description: "benefits.Zurich_Takaful.cashback" },
    { type: "", description: "benefits.Zurich_Takaful.others" }
  ],
  MSIG: [
    { type: "driver", description: "benefits.MSIG.additionalDriver" },
    { type: "24hrTowing", description: "benefits.MSIG.emergencyTowing" },
    { type: "free", description: "benefits.MSIG.batteryService" },
    { type: "free", description: "benefits.MSIG.tankService" },
    { type: "free", description: "benefits.MSIG.tyreService" },
    { type: "free", description: "benefits.MSIG.roadsideAssistance" },
    { type: "free", description: "benefits.MSIG.claimAssistance" },
    { type: "free", description: "benefits.MSIG.lockSmith" },
    { type: "", description: "benefits.MSIG.eHailing" },
    { type: "", description: "benefits.MSIG.instantApproval" },
    { type: "", description: "benefits.MSIG.easyAndFastClaims" },
    { type: "", description: "benefits.MSIG.coverForFEL" },
    { type: "", description: "benefits.MSIG.coverForTheft" },
    { type: "", description: "benefits.MSIG.oldVehicles" },
    { type: "", description: "benefits.MSIG.cashback" },
    { type: "", description: "benefits.MSIG.others" }
  ],
  Kurnia: [
    { type: "driver", description: "benefits.Kurnia.additionalDriver" },
    { type: "24hrTowing", description: "benefits.Kurnia.emergencyTowing" },
    { type: "free", description: "benefits.Kurnia.batteryService" },
    { type: "free", description: "benefits.Kurnia.tankService" },
    { type: "free", description: "benefits.Kurnia.tyreService" },
    { type: "free", description: "benefits.Kurnia.roadsideAssistance" },
    { type: "free", description: "benefits.Kurnia.claimAssistance" },
    { type: "free", description: "benefits.Kurnia.lockSmith" },
    { type: "", description: "benefits.Kurnia.eHailing" },
    { type: "", description: "benefits.Kurnia.instantApproval" },
    { type: "", description: "benefits.Kurnia.easyAndFastClaims" },
    { type: "", description: "benefits.Kurnia.coverForFEL" },
    { type: "", description: "benefits.Kurnia.coverForTheft" },
    { type: "", description: "benefits.Kurnia.oldVehicles" },
    { type: "", description: "benefits.Kurnia.cashback" },
    { type: "", description: "benefits.Kurnia.others" }
  ],
  RHB: [
    { type: "driver", description: "benefits.RHB.additionalDriver" },
    { type: "24hrTowing", description: "benefits.RHB.emergencyTowing" },
    {
      type: "24hrTowing",
      description: "benefits.RHB.emergencyTowingAccidentCases"
    },
    { type: "free", description: "benefits.RHB.batteryService" },
    { type: "free", description: "benefits.RHB.tankService" },
    { type: "free", description: "benefits.RHB.tyreService" },
    { type: "free", description: "benefits.RHB.roadsideAssistance" },
    { type: "free", description: "benefits.RHB.claimAssistance" },
    { type: "free", description: "benefits.RHB.lockSmith" },
    { type: "", description: "benefits.RHB.eHailing" },
    { type: "", description: "benefits.RHB.instantApproval" },
    { type: "", description: "benefits.RHB.easyAndFastClaims" },
    { type: "", description: "benefits.RHB.coverForFEL" },
    { type: "", description: "benefits.RHB.coverForTheft" },
    { type: "", description: "benefits.RHB.oldVehicles" },
    { type: "", description: "benefits.RHB.cashback" },
    { type: "", description: "benefits.RHB.others" }
  ],
  Tune: [
    { type: "driver", description: "benefits.Tune.additionalDriver" },
    { type: "24hrTowing", description: "benefits.Tune.emergencyTowing" },
    { type: "free", description: "benefits.Tune.batteryService" },
    { type: "free", description: "benefits.Tune.tankService" },
    { type: "free", description: "benefits.Tune.tyreService" },
    { type: "free", description: "benefits.Tune.roadsideAssistance" },
    { type: "free", description: "benefits.Tune.roadsideRepair" },
    { type: "free", description: "benefits.Tune.claimAssistance" },
    { type: "free", description: "benefits.Tune.lockSmith" },
    { type: "", description: "benefits.Tune.eHailing" },
    { type: "", description: "benefits.Tune.instantApproval" },
    { type: "", description: "benefits.Tune.easyAndFastClaims" },
    { type: "", description: "benefits.Tune.coverForFEL" },
    { type: "", description: "benefits.Tune.coverForTheft" },
    { type: "", description: "benefits.Tune.oldVehicles" },
    { type: "", description: "benefits.Tune.cashback" },
    { type: "", description: "benefits.Tune.others" }
  ],
  Etiqa: [
    { type: "driver", description: "benefits.Etiqa.additionalDriver" },
    { type: "24hrTowing", description: "benefits.Etiqa.emergencyTowing" },
    { type: "free", description: "benefits.Etiqa.batteryService" },
    { type: "free", description: "benefits.Etiqa.tankService" },
    { type: "free", description: "benefits.Etiqa.tyreService" },
    { type: "free", description: "benefits.Etiqa.roadsideAssistance" },
    { type: "free", description: "benefits.Etiqa.claimAssistance" },
    { type: "free", description: "benefits.Etiqa.lockSmith" },
    { type: "", description: "benefits.Etiqa.eHailing" },
    { type: "", description: "benefits.Etiqa.instantApproval" },
    { type: "", description: "benefits.Etiqa.easyAndFastClaims" },
    { type: "", description: "benefits.Etiqa.coverForFEL" },
    { type: "", description: "benefits.Etiqa.coverForTheft" },
    { type: "", description: "benefits.Etiqa.oldVehicles" },
    { type: "", description: "benefits.Etiqa.cashback" },
    { type: "", description: "benefits.Etiqa.others" }
  ],
    Etiqa_Takaful: [
    { type: "driver", description: "benefits.Etiqa.additionalDriver" },
    { type: "24hrTowing", description: "benefits.Etiqa.emergencyTowing" },
    { type: "free", description: "benefits.Etiqa.batteryService" },
    { type: "free", description: "benefits.Etiqa.tankService" },
    { type: "free", description: "benefits.Etiqa.tyreService" },
    { type: "free", description: "benefits.Etiqa.roadsideAssistance" },
    { type: "free", description: "benefits.Etiqa.claimAssistance" },
    { type: "free", description: "benefits.Etiqa.lockSmith" },
    { type: "", description: "benefits.Etiqa.eHailing" },
    { type: "", description: "benefits.Etiqa.instantApproval" },
    { type: "", description: "benefits.Etiqa.easyAndFastClaims" },
    { type: "", description: "benefits.Etiqa.coverForFEL" },
    { type: "", description: "benefits.Etiqa.coverForTheft" },
    { type: "", description: "benefits.Etiqa.oldVehicles" },
    { type: "", description: "benefits.Etiqa.cashback" },
    { type: "", description: "benefits.Etiqa.others" },
    { type: "", description: "benefits.Etiqa.eHailing" }
  ],
  Pacific: [
    { type: "driver", description: "benefits.Pacific.additionalDriver" },
    { type: "24hrTowing", description: "benefits.Pacific.emergencyTowing" },
    { type: "free", description: "benefits.Pacific.batteryService" },
    { type: "free", description: "benefits.Pacific.tankService" },
    { type: "free", description: "benefits.Pacific.tyreService" },
    { type: "free", description: "benefits.Pacific.roadsideAssistance" },
    { type: "free", description: "benefits.Pacific.roadsideRepair" },
    { type: "free", description: "benefits.Pacific.claimAssistance" },
    { type: "free", description: "benefits.Pacific.lockSmith" },
    { type: "", description: "benefits.Pacific.eHailing" },
    { type: "", description: "benefits.Pacific.instantApproval" },
    { type: "", description: "benefits.Pacific.easyAndFastClaims" },
    { type: "", description: "benefits.Pacific.coverForFEL" },
    { type: "", description: "benefits.Pacific.coverForTheft" },
    { type: "", description: "benefits.Pacific.oldVehicles" },
    { type: "", description: "benefits.Pacific.cashback" },
    { type: "", description: "benefits.Pacific.others" }
  ],
  Ikhlas: [
    { type: "driver", description: "benefits.Ikhlas.additionalDriver" },
    { type: "24hrTowing", description: "benefits.Ikhlas.emergencyTowing" },
    { type: "free", description: "benefits.Ikhlas.batteryService" },
    { type: "free", description: "benefits.Ikhlas.tankService" },
    { type: "free", description: "benefits.Ikhlas.tyreService" },
    { type: "free", description: "benefits.Ikhlas.roadsideAssistance" },
    { type: "free", description: "benefits.Ikhlas.claimAssistance" },
    { type: "free", description: "benefits.Ikhlas.lockSmith" },
    { type: "", description: "benefits.Ikhlas.eHailing" },
    { type: "", description: "benefits.Ikhlas.instantApproval" },
    { type: "", description: "benefits.Ikhlas.easyAndFastClaims" },
    { type: "", description: "benefits.Ikhlas.coverForFEL" },
    { type: "", description: "benefits.Ikhlas.coverForTheft" },
    { type: "", description: "benefits.Ikhlas.oldVehicles" },
    { type: "", description: "benefits.Ikhlas.cashback" },
    { type: "", description: "benefits.Ikhlas.others" }
  ],
  Sompo: [
    { type: "driver", description: "benefits.Sompo.additionalDriver" },
    { type: "24hrTowing", description: "benefits.Sompo.emergencyTowing" },
    { type: "free", description: "benefits.Sompo.batteryService" },
    { type: "free", description: "benefits.Sompo.tankService" },
    { type: "free", description: "benefits.Sompo.tyreService" },
    { type: "free", description: "benefits.Sompo.roadsideAssistance" },
    { type: "free", description: "benefits.Sompo.claimAssistance" },
    { type: "free", description: "benefits.Sompo.lockSmith" },
    { type: "", description: "benefits.Sompo.eHailing" },
    { type: "", description: "benefits.Sompo.instantApproval" },
    { type: "", description: "benefits.Sompo.easyAndFastClaims" },
    { type: "", description: "benefits.Sompo.coverForFEL" },
    { type: "", description: "benefits.Sompo.coverForTheft" },
    { type: "", description: "benefits.Sompo.oldVehicles" },
    { type: "", description: "benefits.Sompo.cashback" },
    { type: "", description: "benefits.Sompo.others" }
  ],
  Pando: [
    { type: "driver", description: "benefits.Pando.additionalDriver" },
    { type: "24hrTowing", description: "benefits.Pando.emergencyTowing" },
    { type: "free", description: "benefits.Pando.batteryService" },
    { type: "free", description: "benefits.Pando.tankService" },
    { type: "free", description: "benefits.Pando.tyreService" },
    { type: "free", description: "benefits.Pando.roadsideAssistance" },
    { type: "free", description: "benefits.Pando.claimAssistance" },
    { type: "free", description: "benefits.Pando.lockSmith" },
    { type: "", description: "benefits.Pando.eHailing" },
    { type: "", description: "benefits.Pando.instantApproval" },
    { type: "", description: "benefits.Pando.easyAndFastClaims" },
    { type: "", description: "benefits.Pando.coverForFEL" },
    { type: "", description: "benefits.Pando.coverForTheft" },
    { type: "", description: "benefits.Pando.oldVehicles" },
    { type: "", description: "benefits.Pando.cashback" },
    { type: "", description: "benefits.Pando.others" }
  ],
  Takaful_Malaysia: [
    {
      type: "driver",
      description: "benefits.TakafulMalaysia.additionalDriver"
    },
    {
      type: "24hrTowing",
      description: "benefits.TakafulMalaysia.emergencyTowing"
    },
    {
      type: "24hrTowing",
      description: "benefits.TakafulMalaysia.emergencyTowingAccidentCases"
    },
    { type: "free", description: "benefits.TakafulMalaysia.batteryService" },
    { type: "free", description: "benefits.TakafulMalaysia.tankService" },
    { type: "free", description: "benefits.TakafulMalaysia.tyreService" },
    {
      type: "free",
      description: "benefits.TakafulMalaysia.roadsideAssistance"
    },
    { type: "free", description: "benefits.TakafulMalaysia.roadsideRepair" },
    { type: "free", description: "benefits.TakafulMalaysia.claimAssistance" },
    { type: "", description: "benefits.TakafulMalaysia.eHailing" },
    { type: "", description: "benefits.TakafulMalaysia.instantApproval" },
    { type: "", description: "benefits.TakafulMalaysia.easyAndFastClaims" },
    { type: "", description: "benefits.TakafulMalaysia.coverForFEL" },
    { type: "", description: "benefits.TakafulMalaysia.coverForTheft" },
    { type: "", description: "benefits.TakafulMalaysia.oldVehicles" },
    { type: "", description: "benefits.TakafulMalaysia.cashback" },
    { type: "", description: "benefits.TakafulMalaysia.others" }
  ],
  Liberty: [
    { type: "driver", description: "benefits.Liberty.additionalDriver" },
    { type: "towing", description: "benefits.Liberty.emergencyTowing" },
    { type: "free", description: "benefits.Liberty.batteryService" },
    { type: "free", description: "benefits.Liberty.tankService" },
    { type: "free", description: "benefits.Liberty.tyreService" },
    { type: "free", description: "benefits.Liberty.roadsideAssistance" },
    { type: "free", description: "benefits.Liberty.claimAssistance" },
    { type: "free", description: "benefits.Liberty.lockSmith" },
    { type: "", description: "benefits.Liberty.eHailing" },
    { type: "", description: "benefits.Liberty.instantApproval" },
    { type: "", description: "benefits.Liberty.easyAndFastClaims" },
    { type: "", description: "benefits.Liberty.coverForFEL" },
    { type: "", description: "benefits.Liberty.coverForTheft" },
    { type: "", description: "benefits.Liberty.oldVehicles" },
    { type: "", description: "benefits.Liberty.cashback" },
    { type: "", description: "benefits.Liberty.others" }
  ],
  Chubb: [
    { type: "driver", description: "benefits.Chubb.additionalDriver" },
    { type: "towing", description: "benefits.Chubb.emergencyTowing" },
    { type: "free", description: "benefits.Chubb.batteryService" },
    { type: "free", description: "benefits.Chubb.tankService" },
    { type: "free", description: "benefits.Chubb.tyreService" },
    { type: "free", description: "benefits.Chubb.roadsideAssistance" },
    { type: "free", description: "benefits.Chubb.roadsideRepair" },
    { type: "free", description: "benefits.Chubb.claimAssistance" },
    { type: "", description: "benefits.Chubb.eHailing" },
    { type: "", description: "benefits.Chubb.instantApproval" },
    { type: "", description: "benefits.Chubb.easyAndFastClaims" }
  ],
  TMIM: [
    { type: "driver", description: "benefits.TMIM.additionalDriver" },
    { type: "24hrTowing", description: "benefits.TMIM.emergencyTowing1" },
    { type: "24hrTowing", description: "benefits.TMIM.emergencyTowing2" },
    { type: "free", description: "benefits.TMIM.batteryService" },
    { type: "free", description: "benefits.TMIM.tyreService" },
    { type: "free", description: "benefits.TMIM.roadsideAssistance" },
    { type: "", description: "benefits.TMIM.instantApproval" }
  ]
};

export const INSURER_BENEFITS_LIST_LORRY = {
  AXA: [
    { type: "driver", description: "benefitsLorry.AXA.additionalDriver" },
    { type: "24hrTowing", description: "benefitsLorry.AXA.emergencyTowing" },
    {
      type: "24hrTowing",
      description: "benefitsLorry.AXA.emergencyTowingAccidentCases"
    },
    { type: "free", description: "benefitsLorry.AXA.roadsideAssistance" },
    { type: "free", description: "benefitsLorry.AXA.claimAssistance" },
    { type: "", description: "benefitsLorry.AXA.coverForFEL" },
    { type: "", description: "benefitsLorry.AXA.coverForTheft" }
  ],
  Zurich_Takaful: [
    {
      type: "24hrTowing",
      description: "benefitsLorry.Zurich_Takaful.emergencyTowing"
    },
    {
      type: "24hrTowing",
      description: "benefitsLorry.Zurich_Takaful.emergencyTowingAccidentCases"
    },
    {
      type: "free",
      description: "benefitsLorry.Zurich_Takaful.roadsideAssistance"
    },
    {
      type: "free",
      description: "benefitsLorry.Zurich_Takaful.claimAssistance"
    },
    { type: "", description: "benefitsLorry.Zurich_Takaful.coverForFEL" },
    { type: "", description: "benefitsLorry.Zurich_Takaful.coverForTheft" }
  ],
  Takaful_Malaysia: [
    {
      type: "driver",
      description: "benefitsLorry.TakafulMalaysia.additionalDriver"
    },
    {
      type: "24hrTowing",
      description: "benefitsLorry.TakafulMalaysia.emergencyTowing"
    },
    {
      type: "24hrTowing",
      description: "benefitsLorry.TakafulMalaysia.emergencyTowingAccidentCases"
    },
    {
      type: "free",
      description: "benefitsLorry.TakafulMalaysia.roadsideAssistance"
    },
    {
      type: "free",
      description: "benefitsLorry.TakafulMalaysia.claimAssistance"
    },
    { type: "", description: "benefitsLorry.TakafulMalaysia.coverForFEL" },
    { type: "", description: "benefitsLorry.TakafulMalaysia.coverForTheft" }
  ]
};

export const INSURER_BENEFITS_LIST_MOTORCYCLE = {
  RHB: [
    { type: "allRider", description: "benefitsMotorcycle.RHB.allRiders" },
    {
      type: "24hrTowing",
      description:
        "benefitsMotorcycle.RHB.motorcycleEmergencyTowingAccidentCases"
    },
    { type: "free", description: "benefitsMotorcycle.RHB.roadsideAssistance" },
    { type: "", description: "benefitsMotorcycle.RHB.thirdPartyBodilyInjury" }
  ],
  AXA: [
    { type: "allRider", description: "benefitsMotorcycle.AXA.allRiders" },
    {
      type: "24hrTowing",
      description:
        "benefitsMotorcycle.AXA.motorcycleEmergencyTowingAccidentCases"
    },
    { type: "free", description: "benefitsMotorcycle.AXA.roadsideAssistance" },
    { type: "free", description: "benefitsMotorcycle.AXA.roadsideRepair" },
    { type: "", description: "benefitsMotorcycle.AXA.instantApproval" },
    { type: "", description: "benefitsMotorcycle.AXA.thirdPartyBodilyInjury" },
    { type: "", description: "benefitsMotorcycle.AXA.coverForFEL" },
    { type: "", description: "benefitsMotorcycle.AXA.coverForTheft" }
  ],
  Zurich_Takaful: [
    {
      type: "allRider",
      description: "benefitsMotorcycle.Zurich_Takaful.allRiders"
    },
    {
      type: "24hrTowing",
      description:
        "benefitsMotorcycle.Zurich_Takaful.motorcycleEmergencyTowingAccidentCases"
    },
    {
      type: "free",
      description: "benefitsMotorcycle.Zurich_Takaful.roadsideAssistance"
    },
    {
      type: "",
      description: "benefitsMotorcycle.Zurich_Takaful.thirdPartyBodilyInjury"
    },
    { type: "", description: "benefitsMotorcycle.Zurich_Takaful.coverForFEL" }
  ],
  Takaful_Malaysia: [
    {
      type: "allRider",
      description: "benefitsMotorcycle.TakafulMalaysia.allRiders"
    },
    {
      type: "24hrTowingMotorcycle",
      description:
        "benefitsMotorcycle.TakafulMalaysia.motorcycleEmergencyTowing"
    },
    {
      type: "24hrTowingMotorcycle",
      description:
        "benefitsMotorcycle.TakafulMalaysia.motorcycleEmergencyTowingAccidentCases"
    },
    {
      type: "free",
      description: "benefitsMotorcycle.TakafulMalaysia.batteryService"
    },
    {
      type: "free",
      description: "benefitsMotorcycle.TakafulMalaysia.tankService"
    },
    {
      type: "free",
      description: "benefitsMotorcycle.TakafulMalaysia.tyreService"
    },
    {
      type: "free",
      description: "benefitsMotorcycle.TakafulMalaysia.roadsideAssistance"
    },
    {
      type: "free",
      description: "benefitsMotorcycle.TakafulMalaysia.roadsideRepair"
    },
    {
      type: "",
      description: "benefitsMotorcycle.TakafulMalaysia.instantApproval"
    },
    {
      type: "",
      description: "benefitsMotorcycle.TakafulMalaysia.easyAndFastClaims"
    },
    {
      type: "",
      description: "benefitsMotorcycle.TakafulMalaysia.thirdPartyBodilyInjury"
    },
    { type: "", description: "benefitsMotorcycle.TakafulMalaysia.coverForFEL" },
    {
      type: "",
      description: "benefitsMotorcycle.TakafulMalaysia.coverForTheft"
    },
    {
      type: "",
      description: "benefitsMotorcycle.TakafulMalaysia.cashbackNoClaim"
    }
  ]
};

export const INSURER_BENEFITS_LIST_FOODPANDA = {
  RHB: [
    { type: "allRider", description: "benefits.RHB.allRiders" },
    {
      type: "24hrTowing",
      description: "benefits.RHB.motorcycleEmergencyTowingAccidentCases"
    },
    { type: "free", description: "benefits.RHB.roadsideAssistance" },
    { type: "", description: "benefits.RHB.thirdPartyBodilyInjury" }
  ],
  AXA: [
    { type: "allRider", description: "benefits.AXA.allRiders" },
    {
      type: "24hrTowing",
      description: "benefits.AXA.motorcycleEmergencyTowingAccidentCases"
    },
    { type: "free", description: "benefits.AXA.roadsideAssistance" },
    { type: "free", description: "benefits.AXA.roadsideRepair" },
    { type: "", description: "benefits.AXA.instantApproval" },
    { type: "", description: "benefits.AXA.thirdPartyBodilyInjury" },
    { type: "", description: "benefits.AXA.coverForFEL" },
    { type: "", description: "benefits.AXA.coverForTheft" }
  ],
  Takaful_Malaysia: [
    {
      type: "allRider",
      description: "benefits.TakafulMalaysia.allRiders"
    },
    {
      type: "24hrTowingMotorcycle",
      description: "benefits.TakafulMalaysia.motorcycleEmergencyTowing"
    },
    {
      type: "24hrTowingMotorcycle",
      description:
        "benefits.TakafulMalaysia.motorcycleEmergencyTowingAccidentCases"
    },
    { type: "free", description: "benefits.TakafulMalaysia.batteryService" },
    { type: "free", description: "benefits.TakafulMalaysia.tankService" },
    { type: "free", description: "benefits.TakafulMalaysia.tyreService" },
    {
      type: "free",
      description: "benefits.TakafulMalaysia.roadsideAssistance"
    },
    { type: "free", description: "benefits.TakafulMalaysia.roadsideRepair" },
    { type: "", description: "benefits.TakafulMalaysia.instantApproval" },
    {
      type: "",
      description: "benefits.TakafulMalaysia.thirdPartyBodilyInjury"
    },
    { type: "", description: "benefits.TakafulMalaysia.coverForFEL" },
    { type: "", description: "benefits.TakafulMalaysia.coverForTheft" },
    { type: "", description: "benefits.TakafulMalaysia.cashbackNoClaim" }
  ]
};

export const INSURER_BENEFITS_LIST_TPFT = {
  PolicyStreet: [
    {
      type: "driver",
      description: "benefitsTPFT.PolicyStreet.additionalDriver"
    },
    {
      type: "towing",
      description: "benefitsTPFT.PolicyStreet.emergencyTowing"
    },
    { type: "free", description: "benefitsTPFT.PolicyStreet.batteryService" },
    { type: "free", description: "benefitsTPFT.PolicyStreet.tankService" },
    { type: "free", description: "benefitsTPFT.PolicyStreet.tyreService" },
    {
      type: "free",
      description: "benefitsTPFT.PolicyStreet.roadsideAssistance"
    },
    { type: "free", description: "benefitsTPFT.PolicyStreet.claimAssistance" },
    { type: "free", description: "benefitsTPFT.PolicyStreet.lockSmith" },
    { type: "", description: "benefitsTPFT.PolicyStreet.eHailing" },
    { type: "", description: "benefitsTPFT.PolicyStreet.instantApproval" },
    { type: "", description: "benefitsTPFT.PolicyStreet.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.PolicyStreet.coverForFEL" },
    { type: "", description: "benefitsTPFT.PolicyStreet.coverForTheft" },
    { type: "", description: "benefitsTPFT.PolicyStreet.oldVehicles" },
    { type: "", description: "benefitsTPFT.PolicyStreet.cashback" },
    { type: "", description: "benefitsTPFT.PolicyStreet.others" }
  ],
  AXA: [
    { type: "driver", description: "benefitsTPFT.AXA.additionalDriver" },
    { type: "towing", description: "benefitsTPFT.AXA.emergencyTowing" },
    { type: "free", description: "benefitsTPFT.AXA.batteryService" },
    { type: "free", description: "benefitsTPFT.AXA.tankService" },
    { type: "free", description: "benefitsTPFT.AXA.tyreService" },
    { type: "free", description: "benefitsTPFT.AXA.roadsideAssistance" },
    { type: "free", description: "benefitsTPFT.AXA.claimAssistance" },
    { type: "free", description: "benefitsTPFT.AXA.lockSmith" },
    { type: "", description: "benefitsTPFT.AXA.eHailing" },
    { type: "", description: "benefitsTPFT.AXA.instantApproval" },
    { type: "", description: "benefitsTPFT.AXA.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.AXA.coverForFEL" },
    { type: "", description: "benefitsTPFT.AXA.coverForTheft" },
    { type: "", description: "benefitsTPFT.AXA.lossOrDamageProperty" },
    { type: "", description: "benefitsTPFT.AXA.bodilyInjury" },
    { type: "", description: "benefitsTPFT.AXA.accidentalDeath" },
    { type: "", description: "benefitsTPFT.AXA.oldVehicles" },
    { type: "", description: "benefitsTPFT.AXA.cashback" },
    { type: "", description: "benefitsTPFT.AXA.others" }
  ],
  Generali: [
    { type: "driver", description: "benefitsTPFT.Generali.additionalDriver" },
    { type: "towing", description: "benefitsTPFT.Generali.emergencyTowing" },
    { type: "free", description: "benefitsTPFT.Generali.batteryService" },
    { type: "free", description: "benefitsTPFT.Generali.tankService" },
    { type: "free", description: "benefitsTPFT.Generali.tyreService" },
    { type: "free", description: "benefitsTPFT.Generali.roadsideAssistance" },
    { type: "free", description: "benefitsTPFT.Generali.claimAssistance" },
    { type: "free", description: "benefitsTPFT.Generali.lockSmith" },
    { type: "", description: "benefitsTPFT.Generali.eHailing" },
    { type: "", description: "benefitsTPFT.Generali.instantApproval" },
    { type: "", description: "benefitsTPFT.Generali.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.Generali.coverForFEL" },
    { type: "", description: "benefitsTPFT.Generali.coverForTheft" },
    { type: "", description: "benefitsTPFT.Generali.lossOrDamageProperty" },
    { type: "", description: "benefitsTPFT.Generali.bodilyInjury" },
    { type: "", description: "benefitsTPFT.Generali.accidentalDeath" },
    { type: "", description: "benefitsTPFT.Generali.oldVehicles" },
    { type: "", description: "benefitsTPFT.Generali.cashback" },
    { type: "", description: "benefitsTPFT.Generali.others" }
  ],
  GrabAXA: [
    { type: "free", description: "benefitsTPFT.AXAGrab.warranty" },
    { type: "free", description: "benefitsTPFT.AXAGrab.roadsideAssistance" },
    { type: "free", description: "benefitsTPFT.AXAGrab.rideupTo30KM" },
    { type: "free", description: "benefitsTPFT.AXAGrab.axaPreferredWorkshop" },
    { type: "", description: "benefitsTPFT.AXAGrab.instantApproval" },
    { type: "", description: "benefitsTPFT.AXAGrab.fastClaimApproval" },
    { type: "", description: "benefitsTPFT.AXAGrab.personalAccidentCarOwner" },
    { type: "", description: "benefitsTPFT.AXAGrab.medicalClaims" },
    { type: "", description: "benefitsTPFT.AXAGrab.lossOfIncome" },
    { type: "", description: "benefitsTPFT.AXAGrab.loanProtection" },
    { type: "", description: "benefitsTPFT.AXAGrab.wheelAlignment" }
  ],
  Allianz: [
    { type: "driver", description: "benefitsTPFT.Allianz.additionalDriver" },
    { type: "towing", description: "benefitsTPFT.Allianz.emergencyTowing" },
    { type: "free", description: "benefitsTPFT.Allianz.batteryService" },
    { type: "free", description: "benefitsTPFT.Allianz.tankService" },
    { type: "free", description: "benefitsTPFT.Allianz.tyreService" },
    { type: "free", description: "benefitsTPFT.Allianz.roadsideAssistance" },
    { type: "free", description: "benefitsTPFT.Allianz.claimAssistance" },
    { type: "free", description: "benefitsTPFT.Allianz.lockSmith" },
    { type: "", description: "benefitsTPFT.Allianz.eHailing" },
    { type: "", description: "benefitsTPFT.Allianz.instantApproval" },
    { type: "", description: "benefitsTPFT.Allianz.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.Allianz.coverForFEL" },
    { type: "", description: "benefitsTPFT.Allianz.coverForTheft" },
    { type: "", description: "benefitsTPFT.Allianz.lossOrDamageProperty" },
    { type: "", description: "benefitsTPFT.Allianz.bodilyInjury" },
    { type: "", description: "benefitsTPFT.Allianz.accidentalDeath" },
    { type: "", description: "benefitsTPFT.Allianz.oldVehicles" },
    { type: "", description: "benefitsTPFT.Allianz.cashback" },
    { type: "", description: "benefitsTPFT.Allianz.others" }
  ],
  Zurich: [
    { type: "driver", description: "benefitsTPFT.Zurich.additionalDriver" },
    { type: "towing", description: "benefitsTPFT.Zurich.emergencyTowing" },
    { type: "free", description: "benefitsTPFT.Zurich.batteryService" },
    { type: "free", description: "benefitsTPFT.Zurich.tankService" },
    { type: "free", description: "benefitsTPFT.Zurich.tyreService" },
    { type: "free", description: "benefitsTPFT.Zurich.roadsideAssistance" },
    { type: "free", description: "benefitsTPFT.Zurich.claimAssistance" },
    { type: "free", description: "benefitsTPFT.Zurich.lockSmith" },
    { type: "", description: "benefitsTPFT.Zurich.eHailing" },
    { type: "", description: "benefitsTPFT.Zurich.instantApproval" },
    { type: "", description: "benefitsTPFT.Zurich.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.Zurich.coverForFEL" },
    { type: "", description: "benefitsTPFT.Zurich.coverForTheft" },
    { type: "", description: "benefitsTPFT.Zurich.lossOrDamageProperty" },
    { type: "", description: "benefitsTPFT.Zurich.bodilyInjury" },
    { type: "", description: "benefitsTPFT.Zurich.oldVehicles" },
    { type: "", description: "benefitsTPFT.Zurich.cashback" },
    { type: "", description: "benefitsTPFT.Zurich.others" }
  ],
  Zurich_Takaful: [
    { type: "towing", description: "benefitsTPFT.Zurich.emergencyTowing" },
    { type: "free", description: "benefitsTPFT.Zurich.batteryService" },
    { type: "free", description: "benefitsTPFT.Zurich.tankService" },
    { type: "free", description: "benefitsTPFT.Zurich.tyreService" },
    { type: "free", description: "benefitsTPFT.Zurich.roadsideAssistance" },
    { type: "free", description: "benefitsTPFT.Zurich.claimAssistance" },
    { type: "free", description: "benefitsTPFT.Zurich.lockSmith" },
    { type: "", description: "benefitsTPFT.Zurich.eHailing" },
    { type: "", description: "benefitsTPFT.Zurich.instantApproval" },
    { type: "", description: "benefitsTPFT.Zurich.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.Zurich.coverForFEL" },
    { type: "", description: "benefitsTPFT.Zurich.coverForTheft" },
    { type: "", description: "benefitsTPFT.Zurich.lossOrDamageProperty" },
    { type: "", description: "benefitsTPFT.Zurich.bodilyInjury" },
    { type: "", description: "benefitsTPFT.Zurich.oldVehicles" },
    { type: "", description: "benefitsTPFT.Zurich.cashback" },
    { type: "", description: "benefitsTPFT.Zurich.others" }
  ],
  MSIG: [
    { type: "driver", description: "benefitsTPFT.MSIG.additionalDriver" },
    { type: "towing", description: "benefitsTPFT.MSIG.emergencyTowing" },
    { type: "free", description: "benefitsTPFT.MSIG.batteryService" },
    { type: "free", description: "benefitsTPFT.MSIG.tankService" },
    { type: "free", description: "benefitsTPFT.MSIG.tyreService" },
    { type: "free", description: "benefitsTPFT.MSIG.roadsideAssistance" },
    { type: "free", description: "benefitsTPFT.MSIG.claimAssistance" },
    { type: "free", description: "benefitsTPFT.MSIG.lockSmith" },
    { type: "", description: "benefitsTPFT.MSIG.eHailing" },
    { type: "", description: "benefitsTPFT.MSIG.instantApproval" },
    { type: "", description: "benefitsTPFT.MSIG.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.MSIG.coverForFEL" },
    { type: "", description: "benefitsTPFT.MSIG.coverForTheft" },
    { type: "", description: "benefitsTPFT.MSIG.lossOrDamageProperty" },
    { type: "", description: "benefitsTPFT.MSIG.bodilyInjury" },
    { type: "", description: "benefitsTPFT.MSIG.accidentalDeath" },
    { type: "", description: "benefitsTPFT.MSIG.oldVehicles" },
    { type: "", description: "benefitsTPFT.MSIG.cashback" },
    { type: "", description: "benefitsTPFT.MSIG.others" }
  ],
  Kurnia: [
    { type: "driver", description: "benefitsTPFT.Kurnia.additionalDriver" },
    { type: "towing", description: "benefitsTPFT.Kurnia.emergencyTowing" },
    { type: "free", description: "benefitsTPFT.Kurnia.batteryService" },
    { type: "free", description: "benefitsTPFT.Kurnia.tankService" },
    { type: "free", description: "benefitsTPFT.Kurnia.tyreService" },
    { type: "free", description: "benefitsTPFT.Kurnia.roadsideAssistance" },
    { type: "free", description: "benefitsTPFT.Kurnia.claimAssistance" },
    { type: "free", description: "benefitsTPFT.Kurnia.lockSmith" },
    { type: "", description: "benefitsTPFT.Kurnia.eHailing" },
    { type: "", description: "benefitsTPFT.Kurnia.instantApproval" },
    { type: "", description: "benefitsTPFT.Kurnia.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.Kurnia.coverForFEL" },
    { type: "", description: "benefitsTPFT.Kurnia.coverForTheft" },
    { type: "", description: "benefitsTPFT.Kurnia.oldVehicles" },
    { type: "", description: "benefitsTPFT.Kurnia.cashback" },
    { type: "", description: "benefitsTPFT.Kurnia.others" }
  ],
  RHB: [
    { type: "driver", description: "benefitsTPFT.RHB.additionalDriver" },
    { type: "towing", description: "benefitsTPFT.RHB.emergencyTowing" },
    { type: "free", description: "benefitsTPFT.RHB.batteryService" },
    { type: "free", description: "benefitsTPFT.RHB.tankService" },
    { type: "free", description: "benefitsTPFT.RHB.tyreService" },
    { type: "free", description: "benefitsTPFT.RHB.roadsideAssistance" },
    { type: "free", description: "benefitsTPFT.RHB.claimAssistance" },
    { type: "free", description: "benefitsTPFT.RHB.lockSmith" },
    { type: "", description: "benefitsTPFT.RHB.eHailing" },
    { type: "", description: "benefitsTPFT.RHB.instantApproval" },
    { type: "", description: "benefitsTPFT.RHB.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.RHB.coverForFEL" },
    { type: "", description: "benefitsTPFT.RHB.coverForTheft" },
    { type: "", description: "benefitsTPFT.RHB.lossOrDamageProperty" },
    { type: "", description: "benefitsTPFT.RHB.bodilyInjury" },
    { type: "", description: "benefitsTPFT.RHB.accidentalDeath" },
    { type: "", description: "benefitsTPFT.RHB.oldVehicles" },
    { type: "", description: "benefitsTPFT.RHB.cashback" },
    { type: "", description: "benefitsTPFT.RHB.others" }
  ],
  Tune: [
    { type: "driver", description: "benefitsTPFT.Tune.additionalDriver" },
    { type: "towing", description: "benefitsTPFT.Tune.emergencyTowing" },
    { type: "free", description: "benefitsTPFT.Tune.batteryService" },
    { type: "free", description: "benefitsTPFT.Tune.tankService" },
    { type: "free", description: "benefitsTPFT.Tune.tyreService" },
    { type: "free", description: "benefitsTPFT.Tune.roadsideAssistance" },
    { type: "free", description: "benefitsTPFT.Tune.claimAssistance" },
    { type: "free", description: "benefitsTPFT.Tune.lockSmith" },
    { type: "", description: "benefitsTPFT.Tune.eHailing" },
    { type: "", description: "benefitsTPFT.Tune.instantApproval" },
    { type: "", description: "benefitsTPFT.Tune.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.Tune.coverForFEL" },
    { type: "", description: "benefitsTPFT.Tune.coverForTheft" },
    { type: "", description: "benefitsTPFT.Tune.lossOrDamageProperty" },
    { type: "", description: "benefitsTPFT.Tune.bodilyInjury" },
    { type: "", description: "benefitsTPFT.Tune.accidentalDeath" },
    { type: "", description: "benefitsTPFT.Tune.oldVehicles" },
    { type: "", description: "benefitsTPFT.Tune.cashback" },
    { type: "", description: "benefitsTPFT.Tune.others" }
  ],
  Etiqa: [
    { type: "driver", description: "benefitsTPFT.Etiqa.additionalDriver" },
    { type: "towing", description: "benefitsTPFT.Etiqa.emergencyTowing" },
    { type: "free", description: "benefitsTPFT.Etiqa.batteryService" },
    { type: "free", description: "benefitsTPFT.Etiqa.tankService" },
    { type: "free", description: "benefitsTPFT.Etiqa.tyreService" },
    { type: "free", description: "benefitsTPFT.Etiqa.roadsideAssistance" },
    { type: "free", description: "benefitsTPFT.Etiqa.claimAssistance" },
    { type: "free", description: "benefitsTPFT.Etiqa.lockSmith" },
    { type: "", description: "benefitsTPFT.Etiqa.eHailing" },
    { type: "", description: "benefitsTPFT.Etiqa.instantApproval" },
    { type: "", description: "benefitsTPFT.Etiqa.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.Etiqa.coverForFEL" },
    { type: "", description: "benefitsTPFT.Etiqa.coverForTheft" },
    { type: "", description: "benefitsTPFT.Etiqa.oldVehicles" },
    { type: "", description: "benefitsTPFT.Etiqa.cashback" },
    { type: "", description: "benefitsTPFT.Etiqa.others" }
  ],
  Etiqa_Takaful: [
    { type: "driver", description: "benefitsTPFT.Etiqa.additionalDriver" },
    { type: "towing", description: "benefitsTPFT.Etiqa.emergencyTowing" },
    { type: "free", description: "benefitsTPFT.Etiqa.batteryService" },
    { type: "free", description: "benefitsTPFT.Etiqa.tankService" },
    { type: "free", description: "benefitsTPFT.Etiqa.tyreService" },
    { type: "free", description: "benefitsTPFT.Etiqa.roadsideAssistance" },
    { type: "free", description: "benefitsTPFT.Etiqa.claimAssistance" },
    { type: "free", description: "benefitsTPFT.Etiqa.lockSmith" },
    { type: "", description: "benefitsTPFT.Etiqa.eHailing" },
    { type: "", description: "benefitsTPFT.Etiqa.instantApproval" },
    { type: "", description: "benefitsTPFT.Etiqa.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.Etiqa.coverForFEL" },
    { type: "", description: "benefitsTPFT.Etiqa.coverForTheft" },
    { type: "", description: "benefitsTPFT.Etiqa.oldVehicles" },
    { type: "", description: "benefitsTPFT.Etiqa.cashback" },
    { type: "", description: "benefitsTPFT.Etiqa.others" },
    { type: "", description: "benefitsTPFT.Etiqa.eHailing" }
  ],
  Pacific: [
    { type: "driver", description: "benefitsTPFT.Pacific.additionalDriver" },
    { type: "towing", description: "benefitsTPFT.Pacific.emergencyTowing" },
    { type: "free", description: "benefitsTPFT.Pacific.batteryService" },
    { type: "free", description: "benefitsTPFT.Pacific.tankService" },
    { type: "free", description: "benefitsTPFT.Pacific.tyreService" },
    { type: "free", description: "benefitsTPFT.Pacific.roadsideAssistance" },
    { type: "free", description: "benefitsTPFT.Pacific.claimAssistance" },
    { type: "free", description: "benefitsTPFT.Pacific.lockSmith" },
    { type: "", description: "benefitsTPFT.Pacific.eHailing" },
    { type: "", description: "benefitsTPFT.Pacific.instantApproval" },
    { type: "", description: "benefitsTPFT.Pacific.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.Pacific.coverForFEL" },
    { type: "", description: "benefitsTPFT.Pacific.coverForTheft" },
    { type: "", description: "benefitsTPFT.Pacific.lossOrDamageProperty" },
    { type: "", description: "benefitsTPFT.Pacific.bodilyInjury" },
    { type: "", description: "benefitsTPFT.Pacific.oldVehicles" },
    { type: "", description: "benefitsTPFT.Pacific.cashback" },
    { type: "", description: "benefitsTPFT.Pacific.others" }
  ],
  Ikhlas: [
    { type: "driver", description: "benefitsTPFT.Ikhlas.additionalDriver" },
    { type: "towing", description: "benefitsTPFT.Ikhlas.emergencyTowing" },
    { type: "free", description: "benefitsTPFT.Ikhlas.batteryService" },
    { type: "free", description: "benefitsTPFT.Ikhlas.tankService" },
    { type: "free", description: "benefitsTPFT.Ikhlas.tyreService" },
    { type: "free", description: "benefitsTPFT.Ikhlas.roadsideAssistance" },
    { type: "free", description: "benefitsTPFT.Ikhlas.claimAssistance" },
    { type: "free", description: "benefitsTPFT.Ikhlas.lockSmith" },
    { type: "", description: "benefitsTPFT.Ikhlas.eHailing" },
    { type: "", description: "benefitsTPFT.Ikhlas.instantApproval" },
    { type: "", description: "benefitsTPFT.Ikhlas.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.Ikhlas.coverForFEL" },
    { type: "", description: "benefitsTPFT.Ikhlas.coverForTheft" },
    { type: "", description: "benefitsTPFT.Ikhlas.lossOrDamageProperty" },
    { type: "", description: "benefitsTPFT.Ikhlas.bodilyInjury" },
    { type: "", description: "benefitsTPFT.Ikhlas.oldVehicles" },
    { type: "", description: "benefitsTPFT.Ikhlas.cashback" },
    { type: "", description: "benefitsTPFT.Ikhlas.others" }
  ],
  Sompo: [
    { type: "driver", description: "benefitsTPFT.Sompo.additionalDriver" },
    { type: "towing", description: "benefitsTPFT.Sompo.emergencyTowing" },
    { type: "free", description: "benefitsTPFT.Sompo.batteryService" },
    { type: "free", description: "benefitsTPFT.Sompo.tankService" },
    { type: "free", description: "benefitsTPFT.Sompo.tyreService" },
    { type: "free", description: "benefitsTPFT.Sompo.roadsideAssistance" },
    { type: "free", description: "benefitsTPFT.Sompo.claimAssistance" },
    { type: "free", description: "benefitsTPFT.Sompo.lockSmith" },
    { type: "", description: "benefitsTPFT.Sompo.eHailing" },
    { type: "", description: "benefitsTPFT.Sompo.instantApproval" },
    { type: "", description: "benefitsTPFT.Sompo.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.Sompo.coverForFEL" },
    { type: "", description: "benefitsTPFT.Sompo.coverForTheft" },
    { type: "", description: "benefitsTPFT.Sompo.lossOrDamageProperty" },
    { type: "", description: "benefitsTPFT.Sompo.bodilyInjury" },
    { type: "", description: "benefitsTPFT.Sompo.oldVehicles" },
    { type: "", description: "benefitsTPFT.Sompo.cashback" },
    { type: "", description: "benefitsTPFT.Sompo.others" }
  ],
  Pando: [
    { type: "driver", description: "benefitsTPFT.Pando.additionalDriver" },
    { type: "towing", description: "benefitsTPFT.Pando.emergencyTowing" },
    { type: "free", description: "benefitsTPFT.Pando.batteryService" },
    { type: "free", description: "benefitsTPFT.Pando.tankService" },
    { type: "free", description: "benefitsTPFT.Pando.tyreService" },
    { type: "free", description: "benefitsTPFT.Pando.roadsideAssistance" },
    { type: "free", description: "benefitsTPFT.Pando.claimAssistance" },
    { type: "free", description: "benefitsTPFT.Pando.lockSmith" },
    { type: "", description: "benefitsTPFT.Pando.eHailing" },
    { type: "", description: "benefitsTPFT.Pando.instantApproval" },
    { type: "", description: "benefitsTPFT.Pando.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.Pando.coverForFEL" },
    { type: "", description: "benefitsTPFT.Pando.coverForTheft" },
    { type: "", description: "benefitsTPFT.Pando.legalFees" },
    { type: "", description: "benefitsTPFT.Pando.lossOrDamageProperty" },
    { type: "", description: "benefitsTPFT.Pando.bodilyInjury" },
    { type: "", description: "benefitsTPFT.Pando.accidentalDeath" },
    { type: "", description: "benefitsTPFT.Pando.oldVehicles" },
    { type: "", description: "benefitsTPFT.Pando.cashback" },
    { type: "", description: "benefitsTPFT.Pando.others" }
  ],
  Takaful_Malaysia: [
    {
      type: "driver",
      description: "benefitsTPFT.TakafulMalaysia.additionalDriver"
    },
    {
      type: "towing",
      description: "benefitsTPFT.TakafulMalaysia.emergencyTowing"
    },
    {
      type: "free",
      description: "benefitsTPFT.TakafulMalaysia.batteryService"
    },
    { type: "free", description: "benefitsTPFT.TakafulMalaysia.tankService" },
    { type: "free", description: "benefitsTPFT.TakafulMalaysia.tyreService" },
    {
      type: "free",
      description: "benefitsTPFT.TakafulMalaysia.roadsideAssistance"
    },
    {
      type: "free",
      description: "benefitsTPFT.TakafulMalaysia.claimAssistance"
    },
    { type: "free", description: "benefitsTPFT.TakafulMalaysia.lockSmith" },
    { type: "", description: "benefitsTPFT.TakafulMalaysia.eHailing" },
    { type: "", description: "benefitsTPFT.TakafulMalaysia.instantApproval" },
    { type: "", description: "benefitsTPFT.TakafulMalaysia.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.TakafulMalaysia.coverForFEL" },
    { type: "", description: "benefitsTPFT.TakafulMalaysia.coverForTheft" },
    { type: "", description: "benefitsTPFT.TakafulMalaysia.oldVehicles" },
    { type: "", description: "benefitsTPFT.TakafulMalaysia.cashback" },
    { type: "", description: "benefitsTPFT.TakafulMalaysia.others" }
  ],
  Liberty: [
    { type: "driver", description: "benefitsTPFT.Liberty.additionalDriver" },
    { type: "towing", description: "benefitsTPFT.Liberty.emergencyTowing" },
    { type: "free", description: "benefitsTPFT.Liberty.batteryService" },
    { type: "free", description: "benefitsTPFT.Liberty.tankService" },
    { type: "free", description: "benefitsTPFT.Liberty.tyreService" },
    { type: "free", description: "benefitsTPFT.Liberty.roadsideAssistance" },
    { type: "free", description: "benefitsTPFT.Liberty.claimAssistance" },
    { type: "free", description: "benefitsTPFT.Liberty.lockSmith" },
    { type: "", description: "benefitsTPFT.Liberty.eHailing" },
    { type: "", description: "benefitsTPFT.Liberty.instantApproval" },
    { type: "", description: "benefitsTPFT.Liberty.easyAndFastClaims" },
    { type: "", description: "benefitsTPFT.Liberty.coverForFEL" },
    { type: "", description: "benefitsTPFT.Liberty.coverForTheft" },
    { type: "", description: "benefitsTPFT.Liberty.oldVehicles" },
    { type: "", description: "benefitsTPFT.Liberty.cashback" },
    { type: "", description: "benefitsTPFT.Liberty.others" }
  ],
  Chubb: [
    { type: "check", description: "benefitsTPFT.Chubb.coverForLoss" },
    { type: "check", description: "benefitsTPFT.Chubb.emergencyTowing" },
    { type: "check", description: "benefitsTPFT.Chubb.replacementCarService" },
    { type: "check", description: "benefitsTPFT.Chubb.hotelReimbursement" },
    { type: "check", description: "benefitsTPFT.Chubb.cashRelief" },
    { type: "", description: "benefitsTPFT.Chubb.liabilityToThirdParty" },
    { type: "", description: "benefitsTPFT.Chubb.minorRepairService" },
    { type: "", description: "benefitsTPFT.Chubb.additionalDriver" },
    { type: "sectionTitle", description: "benefitsTPFT.Chubb.convenience" },
    { type: "check", description: "benefitsTPFT.Chubb.digitalClaimPortal" },
    { type: "", description: "benefitsTPFT.Chubb.nationwidePanelWorkshop" }
  ],
  TMIM: [
    { type: "driver", description: "benefitsTPFT.TMIM.additionalDriver" },
    { type: "towing", description: "benefitsTPFT.TMIM.emergencyTowing1" },
    { type: "towing", description: "benefitsTPFT.TMIM.emergencyTowing2" },
    { type: "free", description: "benefitsTPFT.TMIM.batteryService" },
    { type: "free", description: "benefitsTPFT.TMIM.tyreService" },
    { type: "free", description: "benefitsTPFT.TMIM.roadsideAssistance" },
    { type: "", description: "benefitsTPFT.TMIM.instantApproval" }
  ]
};
